import { useEffect, useState, Fragment } from "react";
import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import LaraSettingsCard from "./LaraSettingsCard";
import AIUsagePanel from "./AIUsagePanel";
import {
  SlidersHorizontal, Bot, ShoppingBag, PhoneCall, UserRound,
  Save, Loader2, AlertTriangle, RotateCcw, Lock, Cpu,
  Sparkles, Play, Plus, Pencil, Trash2, Download, ChevronDown, X, ListChecks,
} from "lucide-react";

const FEATURE_ICON = {
  order_ai_timeline:   ShoppingBag,
  call_tagger:         PhoneCall,
  customer_ai_summary: UserRound,
  lara_quote_analysis: Sparkles,
  lara_quote_chat:     Sparkles,
};

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors ${
        checked
          ? "bg-emerald-500/25 border-emerald-500/50"
          : "bg-white/[0.04] border-white/15"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
          checked ? "translate-x-6 bg-emerald-300" : "translate-x-1 bg-slate-400"
        }`}
      />
    </button>
  );
}

function FeatureCard({ feature, supportedModels, onSaved }) {
  const Icon = FEATURE_ICON[feature.key] || Bot;
  const [prompt, setPrompt] = useState(feature.system_prompt || "");
  const [maxItems, setMaxItems] = useState(feature.max_input_items ?? 1);
  const [enabled, setEnabled] = useState(feature.enabled !== false);
  const [modelKey, setModelKey] = useState(`${feature.provider}|${feature.model}`);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPrompt(feature.system_prompt || "");
    setMaxItems(feature.max_input_items ?? 1);
    setEnabled(feature.enabled !== false);
    setModelKey(`${feature.provider}|${feature.model}`);
  }, [feature]);

  const dirty =
    prompt !== (feature.system_prompt || "") ||
    Number(maxItems) !== Number(feature.max_input_items) ||
    enabled !== (feature.enabled !== false) ||
    modelKey !== `${feature.provider}|${feature.model}`;

  const restoreDefault = () => setPrompt(feature.default_system_prompt || "");

  const save = async () => {
    if (!prompt.trim()) {
      toast.error("Prompt cannot be empty");
      return;
    }
    const [provider, model] = modelKey.split("|");
    setSaving(true);
    try {
      const { data } = await api.patch(`/config/ai-features/${feature.key}`, {
        system_prompt: prompt,
        max_input_items: Number(maxItems),
        enabled,
        provider,
        model,
      });
      toast.success(`${feature.label} saved`, {
        description: "Applies to the next run of this feature.",
      });
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex flex-col gap-4"
      data-testid={`ai-config-card-${feature.key}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg border border-purple-500/40 bg-purple-500/[0.06] text-purple-300 grid place-items-center">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-white font-semibold text-sm flex items-center gap-2">
              {feature.label}
            </div>
            <div className="text-[12px] text-slate-400 mt-0.5 max-w-xl">{feature.description}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1.5">
              {feature.trigger}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Toggle checked={enabled} onChange={setEnabled} testid={`ai-config-enabled-${feature.key}`} />
          <span className={`text-[10px] font-mono uppercase tracking-widest ${enabled ? "text-emerald-300" : "text-slate-500"}`}>
            {enabled ? "Enabled" : "Disabled"}
          </span>
        </div>
      </div>

      {/* Output-shape warning — Order AI Timeline only */}
      {feature.output_warning && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-200"
          data-testid={`ai-config-warning-${feature.key}`}
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
          <span>
            This feature&rsquo;s output (the <code className="text-amber-100">intel</code> block —
            <code className="text-amber-100"> risk_score</code>, <code className="text-amber-100">risk_level</code>,
            <code className="text-amber-100"> delivery_risk</code>, <code className="text-amber-100">margin_health</code>,
            <code className="text-amber-100"> tags</code>&hellip;) is stored and rendered on Order 360.
            Renaming or removing a field from the required JSON shape can break that display. Edit the
            wording freely, but keep the JSON structure intact.
          </span>
        </div>
      )}

      {/* Prompt */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            System prompt
          </label>
          <button
            type="button"
            onClick={restoreDefault}
            data-testid={`ai-config-restore-${feature.key}`}
            className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
          >
            <RotateCcw className="w-3 h-3" /> Restore default
          </button>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={10}
          data-testid={`ai-config-prompt-${feature.key}`}
          className="w-full rounded-lg bg-black/30 border border-white/10 focus:border-purple-400/60 outline-none px-3 py-2 text-[12.5px] text-slate-200 font-mono leading-relaxed resize-y"
        />
      </div>

      {/* Controls row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-1.5">
            <Cpu className="w-3 h-3" /> Model
          </label>
          <select
            value={modelKey}
            onChange={(e) => setModelKey(e.target.value)}
            data-testid={`ai-config-model-${feature.key}`}
            className="w-full rounded-lg bg-black/30 border border-white/10 focus:border-purple-400/60 outline-none px-3 py-2 text-[13px] text-white cursor-pointer appearance-none"
          >
            {supportedModels.map((m) => (
              <option key={`${m.provider}|${m.model}`} value={`${m.provider}|${m.model}`} className="bg-slate-900 text-white">
                {m.label} — {m.provider}/{m.model}
              </option>
            ))}
            {!supportedModels.some((m) => `${m.provider}|${m.model}` === modelKey) && (
              <option value={modelKey} className="bg-slate-900 text-white">
                {feature.provider}/{feature.model} (current)
              </option>
            )}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5 block">
            Input limit
          </label>
          <input
            type="number"
            min={1}
            max={200}
            value={maxItems}
            onChange={(e) => setMaxItems(e.target.value)}
            data-testid={`ai-config-maxitems-${feature.key}`}
            className="w-full rounded-lg bg-black/30 border border-white/10 focus:border-purple-400/60 outline-none px-3 py-2 text-[13px] text-white tabular-nums"
          />
          <div className="text-[11px] text-slate-500 mt-1">{feature.input_label}</div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
        <div className="text-[10.5px] font-mono text-slate-600">
          {feature.updated_by
            ? `Last edited by ${feature.updated_by}`
            : "Never edited (using default)"}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`ai-config-save-${feature.key}`}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors ${
            !dirty || saving
              ? "bg-white/[0.04] text-slate-500 cursor-not-allowed border border-white/10"
              : "bg-purple-500/20 text-purple-100 border border-purple-500/50 hover:bg-purple-500/30"
          }`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

const QTYPES = [
  { key: "noul", label: "Yes-No" },
  { key: "choice", label: "Choice" },
  { key: "score", label: "Score" },
];

function parseOptions(text) {
  const out = {};
  (text || "").split("\n").map((l) => l.trim()).filter(Boolean).forEach((l) => {
    const i = l.indexOf(":");
    if (i > 0) out[l.slice(0, i).trim()] = l.slice(i + 1).trim();
    else out[l] = l;
  });
  return out;
}
function parseLevels(text) {
  return (text || "").split(/[\n,]/).map((l) => l.trim()).filter(Boolean);
}

function JevWizard({ entities, draft, onCancel, onSaved }) {
  const [name, setName] = useState(draft?.name || "");
  const [entity, setEntity] = useState(draft?.entity || "customer");
  const [fields, setFields] = useState(draft?.fields || []);
  const [qtype, setQtype] = useState(draft?.question?.type || "noul");
  const [instructions, setInstructions] = useState(draft?.question?.instructions || "");
  const [optionsText, setOptionsText] = useState(
    draft?.question?.options
      ? Object.entries(draft.question.options).map(([k, v]) => (k === v ? k : `${k}: ${v}`)).join("\n")
      : ""
  );
  const [levelsText, setLevelsText] = useState((draft?.question?.levels || []).join(", "));
  const [schedule, setSchedule] = useState(draft?.schedule || "manual");
  const [maxRecords, setMaxRecords] = useState(draft?.max_records ?? 500);
  const [trying, setTrying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tryRows, setTryRows] = useState(null);
  const [openState, setOpenState] = useState(null);

  const entMeta = entities.find((e) => e.key === entity) || { fields: [] };
  const toggleField = (f) => setFields((p) => (p.includes(f) ? p.filter((x) => x !== f) : [...p, f]));

  const buildQuestion = () => {
    const q = { type: qtype, instructions };
    if (qtype === "choice") q.options = parseOptions(optionsText);
    if (qtype === "score") q.levels = parseLevels(levelsText);
    return q;
  };
  const payload = () => ({
    name, entity, fields, question: buildQuestion(),
    schedule, max_records: Number(maxRecords), enabled: draft?.enabled !== false,
  });

  const tryFive = async () => {
    setTrying(true); setTryRows(null);
    try {
      const { data } = await api.post("/config/jev/test", { entity, fields, question: buildQuestion() });
      setTryRows(data.results || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Try failed");
    } finally { setTrying(false); }
  };
  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (draft?.id) await api.patch(`/config/jev/${draft.id}`, payload());
      else await api.post("/config/jev", payload());
      toast.success("Decision saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full rounded-lg bg-black/30 border border-white/10 focus:border-teal-400/60 outline-none px-3 py-2 text-[13px] text-white";
  return (
    <div className="rounded-lg border border-teal-500/30 bg-teal-500/[0.04] p-4 space-y-4" data-testid="jev-wizard">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Decision name (e.g. Reorder likely)"
        data-testid="jev-wizard-name" className={inputCls} />

      {/* Step 1 — applies to + fields */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-teal-300 mb-1.5">1 · Applies to</div>
        <select value={entity} onChange={(e) => { setEntity(e.target.value); setFields([]); setTryRows(null); }}
          data-testid="jev-wizard-entity" className={inputCls + " cursor-pointer mb-2"}>
          {entities.map((e) => <option key={e.key} value={e.key} className="bg-slate-900">{e.label}</option>)}
        </select>
        <div className="flex flex-wrap gap-1.5">
          {entMeta.fields.map((f) => (
            <button key={f.key} type="button" onClick={() => toggleField(f.key)}
              data-testid={`jev-wizard-field-${f.key}`}
              className={`text-[11px] px-2 py-1 rounded border font-mono ${fields.includes(f.key)
                ? "border-teal-500/50 bg-teal-500/15 text-teal-200" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Step 2 — question */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-teal-300 mb-1.5">2 · Question</div>
        <div className="flex gap-1.5 mb-2">
          {QTYPES.map((t) => (
            <button key={t.key} type="button" onClick={() => setQtype(t.key)}
              data-testid={`jev-wizard-qtype-${t.key}`}
              className={`text-[11px] px-2.5 py-1 rounded border font-mono ${qtype === t.key
                ? "border-teal-500/50 bg-teal-500/15 text-teal-200" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2}
          placeholder="Instructions, e.g. Is this customer likely to order again within 30 days?"
          data-testid="jev-wizard-instructions" className={inputCls + " font-mono resize-y mb-2"} />
        {qtype === "choice" && (
          <textarea value={optionsText} onChange={(e) => setOptionsText(e.target.value)} rows={4}
            placeholder={"One option per line (optionally label: description)\nquote request\ndelivery\ncomplaint\ntechnical\nother"}
            data-testid="jev-wizard-options" className={inputCls + " font-mono resize-y"} />
        )}
        {qtype === "score" && (
          <input value={levelsText} onChange={(e) => setLevelsText(e.target.value)}
            placeholder="Levels low → high, comma separated (e.g. low, medium, high, critical)"
            data-testid="jev-wizard-levels" className={inputCls + " font-mono"} />
        )}
      </div>

      {/* Step 3 — schedule + max */}
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-teal-300 mb-1.5">3 · Schedule</div>
          <select value={schedule} onChange={(e) => setSchedule(e.target.value)} data-testid="jev-wizard-schedule"
            className={inputCls + " cursor-pointer w-40"}>
            <option value="manual" className="bg-slate-900">Manual</option>
            <option value="nightly" className="bg-slate-900">Nightly</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Max records</div>
          <input type="number" min={1} max={2000} value={maxRecords} onChange={(e) => setMaxRecords(e.target.value)}
            data-testid="jev-wizard-maxrecords" className={inputCls + " w-28 tabular-nums"} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button type="button" onClick={tryFive} disabled={trying} data-testid="jev-wizard-try"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold bg-white/[0.04] border border-white/15 text-slate-200 hover:bg-white/10 disabled:opacity-50">
          {trying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Try on 5 records
        </button>
        <button type="button" onClick={save} disabled={saving} data-testid="jev-wizard-save"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold bg-teal-500/20 border border-teal-500/50 text-teal-100 hover:bg-teal-500/30 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
        <button type="button" onClick={onCancel} data-testid="jev-wizard-cancel"
          className="px-3 py-2 rounded-lg text-[12px] text-slate-400 hover:text-white">Cancel</button>
      </div>

      {tryRows && (
        <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="jev-wizard-try-results">
          <table className="w-full text-[12px]">
            <thead className="bg-white/[0.03] text-slate-500 text-[10px] font-mono uppercase">
              <tr><th className="text-left px-3 py-1.5">Record</th><th className="text-left px-3 py-1.5">Answer</th>
                <th className="text-left px-3 py-1.5">Conf.</th><th className="px-3 py-1.5"></th></tr>
            </thead>
            <tbody>
              {tryRows.map((r, i) => (
                <Fragment key={r.record_id}>
                  <tr className="border-t border-white/[0.06]">
                    <td className="px-3 py-1.5 text-slate-200">{r.label}</td>
                    <td className="px-3 py-1.5 text-teal-300 font-mono">{r.answer ?? <span className="text-red-400">{r.error}</span>}</td>
                    <td className="px-3 py-1.5 text-slate-400 tabular-nums">{r.confidence != null ? `${Math.round(r.confidence * 100)}%` : "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button type="button" onClick={() => setOpenState(openState === i ? null : i)}
                        className="text-[10px] text-slate-400 hover:text-white font-mono">what Jev saw</button>
                    </td>
                  </tr>
                  {openState === i && (
                    <tr className="bg-black/30"><td colSpan={4} className="px-3 py-2">
                      <pre className="text-[10.5px] font-mono text-slate-300 whitespace-pre-wrap break-words max-h-48 overflow-auto">{JSON.stringify(r.state, null, 2)}</pre>
                    </td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function JevResults({ decision, onClose }) {
  const [data, setData] = useState({ items: [], total: 0, answers: [], per_page: 50 });
  const [answer, setAnswer] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page });
        if (answer) params.set("answer", answer);
        if (q) params.set("q", q);
        const { data } = await api.get(`/config/jev/${decision.id}/results?${params}`);
        if (!cancel) setData(data);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [answer, q, page, decision.id]);

  const csv = `${process.env.REACT_APP_BACKEND_URL}/api/config/jev/${decision.id}/results.csv`;
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid="jev-results">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-white font-semibold text-sm">{decision.name} · results <span className="text-slate-500 font-mono text-xs">({data.total})</span></div>
        <div className="flex items-center gap-2">
          <a href={csv} data-testid="jev-results-csv" className="inline-flex items-center gap-1 text-[12px] font-mono text-slate-400 hover:text-teal-300"><Download className="w-3.5 h-3.5" /> Export CSV</a>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={answer} onChange={(e) => { setAnswer(e.target.value); setPage(1); }} data-testid="jev-results-filter"
          className="text-[12px] bg-black/30 border border-white/10 rounded px-2 py-1 text-slate-200">
          <option value="">All answers</option>
          {data.answers.map((a) => <option key={a} value={a} className="bg-slate-900">{a}</option>)}
        </select>
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search record…"
          data-testid="jev-results-search" className="text-[12px] bg-black/30 border border-white/10 rounded px-2 py-1 text-slate-200 w-40" />
      </div>
      {loading ? <div className="text-slate-500 text-sm py-6 text-center">Loading…</div> : (
        <table className="w-full text-[12px]" data-testid="jev-results-table">
          <thead className="text-slate-500 text-[10px] font-mono uppercase">
            <tr><th className="text-left px-2 py-1.5">Record</th><th className="text-left px-2 py-1.5">Answer</th>
              <th className="text-left px-2 py-1.5">Confidence</th><th className="text-left px-2 py-1.5">Computed</th></tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} className="border-t border-white/[0.06]" data-testid={`jev-result-row-${r.record_id}`}>
                <td className="px-2 py-1.5 text-slate-200">{r.label}</td>
                <td className="px-2 py-1.5 text-teal-300 font-mono">{r.answer}</td>
                <td className="px-2 py-1.5 text-slate-400 tabular-nums">{Math.round((r.confidence || 0) * 100)}%</td>
                <td className="px-2 py-1.5 text-slate-500 font-mono text-[10px]">{(r.computed_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={4} className="text-slate-600 text-center py-6">No results yet — run the decision.</td></tr>}
          </tbody>
        </table>
      )}
      {data.total > data.per_page && (
        <div className="flex items-center justify-end gap-2 text-[12px]">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 text-slate-400 disabled:opacity-40">Prev</button>
          <span className="text-slate-500">Page {page}</span>
          <button disabled={page * data.per_page >= data.total} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 text-slate-400 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

function JevSection() {
  const [status, setStatus] = useState(null);
  const [master, setMaster] = useState(true);
  const [decisions, setDecisions] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(null);
  const [resultsFor, setResultsFor] = useState(null);
  const [runningId, setRunningId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, e] = await Promise.all([
        api.get("/config/jev/status"),
        api.get("/config/jev"),
        api.get("/config/jev/entities"),
      ]);
      setStatus(s.data);
      setMaster(l.data.master_enabled);
      setDecisions(l.data.decisions || []);
      setEntities(e.data.entities || []);
    } catch (err) {
      toast.error("Failed to load Jev config");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleMaster = async (v) => { setMaster(v); await api.post("/config/jev/master", { enabled: v }); };
  const toggleEnabled = async (d, v) => {
    await api.patch(`/config/jev/${d.id}`, { enabled: v });
    setDecisions((prev) => prev.map((x) => (x.id === d.id ? { ...x, enabled: v } : x)));
  };
  const runNow = async (d) => {
    setRunningId(d.id);
    try {
      const { data } = await api.post(`/config/jev/${d.id}/run`);
      toast.success(`Ran "${d.name}" — ${data.processed} record(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Run failed");
    } finally { setRunningId(null); }
  };
  const del = async (d) => {
    if (!window.confirm(`Delete "${d.name}" and its results?`)) return;
    await api.delete(`/config/jev/${d.id}`);
    load();
  };

  const ENT_LABEL = Object.fromEntries(entities.map((e) => [e.key, e.label]));
  const QLABEL = { noul: "Yes-No", choice: "Choice", score: "Score" };

  return (
    <div className="rounded-xl border border-teal-500/25 bg-white/[0.02] p-5 flex flex-col gap-4" data-testid="jev-section">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg border border-teal-500/40 bg-teal-500/[0.06] text-teal-300 grid place-items-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="text-white font-semibold text-sm">Jev decisions</div>
            <div className="text-[12px] text-slate-400 mt-0.5 max-w-xl">
              TypeSafe's typed decision model — classify, score or yes/no your records in bulk. Fast and cheap; billed to your Universal Key.
            </div>
            {status && (status.connected
              ? <div className="text-[11px] text-emerald-300 font-mono mt-1" data-testid="jev-status">Connected via Universal Key</div>
              : <div className="text-[11px] text-red-400 font-mono mt-1" data-testid="jev-status">{status.detail || "Not connected"}</div>)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Toggle checked={master} onChange={toggleMaster} testid="jev-master-toggle" />
          <span className={`text-[10px] font-mono uppercase tracking-widest ${master ? "text-emerald-300" : "text-slate-500"}`}>{master ? "On" : "Off"}</span>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>
      ) : (
        <>
          <div className="space-y-2" data-testid="jev-decision-list">
            {decisions.length === 0 && <div className="text-slate-600 text-sm">No decisions yet.</div>}
            {decisions.map((d) => (
              <div key={d.id} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 flex items-center gap-3 flex-wrap" data-testid={`jev-decision-${d.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="text-white text-[13px] font-medium">{d.name}</div>
                  <div className="text-[10.5px] font-mono text-slate-500">
                    {ENT_LABEL[d.entity] || d.entity} · {QLABEL[d.question?.type]} · {d.schedule}
                    {d.last_run ? ` · ran ${d.last_run.slice(0, 16).replace("T", " ")}` : " · never run"}
                  </div>
                </div>
                <Toggle checked={d.enabled} onChange={(v) => toggleEnabled(d, v)} testid={`jev-decision-enabled-${d.id}`} />
                <button onClick={() => runNow(d)} disabled={runningId === d.id} data-testid={`jev-run-${d.id}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-teal-500/40 text-teal-200 hover:bg-teal-500/15 disabled:opacity-50">
                  {runningId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run now
                </button>
                <button onClick={() => setResultsFor(d)} data-testid={`jev-results-btn-${d.id}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-white/10 text-slate-300 hover:bg-white/5"><ListChecks className="w-3.5 h-3.5" /> Results</button>
                <button onClick={() => setWizard(d)} data-testid={`jev-edit-${d.id}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-white/10 text-slate-300 hover:bg-white/5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => del(d)} data-testid={`jev-delete-${d.id}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-white/10 text-red-300 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>

          {resultsFor && <JevResults decision={resultsFor} onClose={() => setResultsFor(null)} />}

          {wizard ? (
            <JevWizard entities={entities} draft={wizard.id ? wizard : null}
              onCancel={() => setWizard(null)}
              onSaved={() => { setWizard(null); load(); }} />
          ) : (
            <button onClick={() => setWizard({})} data-testid="jev-new-decision"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold bg-teal-500/15 border border-teal-500/40 text-teal-200 hover:bg-teal-500/25 self-start">
              <Plus className="w-4 h-4" /> New decision
            </button>
          )}
        </>
      )}
    </div>
  );
}


export default function AIConfig() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.access_role === "admin";
  const [features, setFeatures] = useState([]);
  const [supportedModels, setSupportedModels] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/config/ai-features");
      setFeatures(data.features || []);
      setSupportedModels(data.supported_models || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load AI config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin]);

  const onSaved = (updated) => {
    setFeatures((prev) => prev.map((f) => (f.key === updated.key ? updated : f)));
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-24 text-center" data-testid="ai-config-denied">
          <Lock className="w-8 h-8 text-slate-500 mx-auto mb-3" />
          <h1 className="text-white font-semibold text-lg">Admin only</h1>
          <p className="text-slate-400 text-sm mt-1">
            AI Config is restricted to administrators.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="ai-config-page">
        {/* Header */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-purple-400 font-mono mb-1 flex items-center gap-1.5">
            <SlidersHorizontal className="w-3 h-3" /> Lara AI · AI Config
          </div>
          <h1 className="text-3xl font-display font-bold tracking-tight">AI Config</h1>
          <div className="text-slate-400 text-sm mt-1 max-w-2xl">
            Tune the system prompt, input limits and on/off state for every credit-using AI
            feature in Talmia. Changes apply to the next run &mdash; existing saved results are
            never rewritten.
          </div>
        </div>

        <AIUsagePanel />

        {loading && !features.length ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-16 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading AI features…
          </div>
        ) : (
          <div className="space-y-5" data-testid="ai-config-list">
            {features.map((f) => (
              <FeatureCard key={f.key} feature={f} supportedModels={supportedModels} onSaved={onSaved} />
            ))}
            <LaraSettingsCard />
            <JevSection />
          </div>
        )}
      </div>
    </Layout>
  );
}
